import { useMutation, useReactiveVar } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { ContentItem as ContentItemModel } from '@deltek/specification-client/core/Models/contentItem'
import { DocumentTemplateView } from '@deltek/specification-client/core/Models/documentTemplate'
import type { TextContentElementInput } from '@deltek/specification-client/core/Models/textContentElement'
import { SetProjectContentItemElements } from 'api/graphQL/mutations/projectProductFamily.mutations'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import {
    reactiveWorkspaceActions,
    setReactiveWorkspaceActions,
} from 'api/graphQL/reactiveVars/workspaceActions'
import {
    isContentTeam,
    setWorkspaceContentItems,
    workspaceContents,
} from 'api/graphQL/reactiveVars/workspaceContent'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import React, { useContext, useState } from 'react'
import { getUpdatedItemsArray } from '../../WorkspaceContentArea'
import { WorkspaceContentContext } from '../../WorkspaceContentContext'
import { AreaContext } from '../AreaContext'
import { TabsContext } from '../TabsContext'
import { getPropertyEditingDetails } from '../addUtils'
import type { NormalizedDocumentTemplateViewItem } from '../model'
import { WorkspaceTabTitles } from '../utils'

export declare type AddNoteSpecifierHandler = {
    (selectedTemplate?: NormalizedDocumentTemplateViewItem): void
}
export type AddNoteSpecifierProps = {
    dtv: Partial<DocumentTemplateView> | null
    tab: number
    template: NormalizedDocumentTemplateViewItem
    contentItemNodes?: Partial<ContentItemModel>[]
    parent?: NormalizedDocumentTemplateViewItem
    selectedTopic?: Partial<ProductFamilyStudioManagement>
    contentTeamFromStudioPage?: boolean
    property: NormalizedDocumentTemplateViewItem
    selectedTemplate?: NormalizedDocumentTemplateViewItem
    currentSpecifierNoteId: string
}

/**
 * `useNoteSpecifier` is a custom React hook that provides functionalities related to note specifiers.
 * the handler `addNoteSpecifier` is published globally via reactiveVars to be used in various components of workspace.
 *
 * @returns {Object} An object containing various properties and methods:
 * - `addNoteSpecifier`: A function to add a note specifier.
 * - `currentSpecifierNoteId`: The ID of the current specifier note.
 * - `optionalHandlers`: An object containing optional handler functions:
 *   - `updateProjectContentItemElement`: func to update project content item element.
 *   - `retrieveUpdatedElementChanges`: func to retrieve updated element changes.
 *   - `handleUpdateTemplate`: func to update the template.
 *   - `handleRetrieveUpdatedDTV`: func to retrieve updated DTV.
 *   - `contentItemWorker`: the contentItem worker.
 * 
 * @example
 * const {
            currentSpecifierNoteId,
            addNoteSpecifier,
        } = useNoteSpecifier()
 */
export function useNoteSpecifier() {
    const isContentTeamUser = useReactiveVar(isContentTeam)
    const { isReviewer } = useReactiveVar(userInfo)
    const { handleUpdateTemplate } = useContext(TabsContext)
    const { handleRetrieveUpdatedDTV, contentItemWorker } = useContext(
        WorkspaceContentContext
    )

    const {
        selectedProjectNode,
        projectId,
        updateExpandState,
        expandStateList,
        retrieveUpdatedElementChanges,
    } = useContext(AreaContext)

    const {
        addNoteSpecifierRequirements: { currentSpecifierNoteId },
    } = reactiveWorkspaceActions()

    const [contentItemForNote, setContentItemForNote] = useState<
        string | undefined
    >()

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const [
        updateProjectContentItemElement,
        { loading: updateNotesLoading },
    ] = useMutation(SetProjectContentItemElements(6), {
        fetchPolicy: 'no-cache',
        onCompleted: async (responseData) => {
            // TODO: create a response type
            return responseData
        },
        onError: (error) => {
            if (error.networkError) {
                showGlobalSnackbar?.({
                    variables: {
                        message:
                            'There was a problem saving your changes. Please copy your recent edits and reconnect to the network.',
                        variant: 'error',
                        id: 'WorkspacePage_SetProjectContentItemElementError',
                    },
                })
            } else if (error.message.includes('character')) {
                showGlobalSnackbar?.({
                    variables: {
                        message: `There was a problem saving your changes. Please remove "|" to save your changes.`,
                        variant: 'error',
                        id: 'WorkspacePage_SetProjectContentItemElementError',
                    },
                })
            } else {
                showGlobalSnackbar?.({
                    variables: {
                        message: `There was a problem saving your changes.`,
                        variant: 'error',
                        id: 'WorkspacePage_SetProjectContentItemElementError',
                    },
                })
            }
        },
    })

    const addNoteSpecifier = React.useCallback(
        (selectedTemplate?: NormalizedDocumentTemplateViewItem) => {
            // destructure requirements to define addSpecifierNote handler
            const {
                addNoteSpecifierRequirements: {
                    dtv,
                    property,
                    tab,
                    template,
                    contentItemNodes,
                    parent,
                    selectedTopic,
                    contentTeamFromStudioPage,
                },
            } = reactiveWorkspaceActions()

            const getParentDetails = () => {
                return {
                    contentItemParentId: property?.parentId ?? parent?.id ?? '',
                    parentKey: parent?.parentKey ?? '',
                    parentType: selectedTopic
                        ? 'None'
                        : property?.parentId === parent?.id
                        ? parent?.type ?? ''
                        : property?.type ?? '',
                }
            }

            const addSpecifierNoteToProjectContentItemElement = () => {
                if (updateNotesLoading) {
                    return
                }

                updateProjectContentItemElement({
                    variables: {
                        input: {
                            ...getPropertyEditingDetails({
                                property: template!,
                                isForArticle:
                                    template?.contentItem?.textType ===
                                    'Article',
                                nodeId: selectedTopic
                                    ? selectedTopic?.projectElementNodeId!
                                    : selectedProjectNode?.id ?? '',
                                ...getParentDetails(),
                                selectedTopic,
                            }),
                            isFromStudioManagementModule: isContentTeamUser,
                            elements: template?.contentItem?.elements?.map(
                                (elementIterator) => {
                                    return {
                                        id: elementIterator.id,
                                        imperialUnits:
                                            elementIterator.imperialUnits,
                                        metricUnits:
                                            elementIterator.metricUnits,
                                        optionItemState:
                                            elementIterator.optionItemState,
                                        text: elementIterator.text,
                                        type: elementIterator.type,
                                        placeholderText:
                                            elementIterator.placeholderText,
                                        changeType: elementIterator.changeType,
                                        authoredUserId:
                                            elementIterator.authoredUserId,
                                        elements: elementIterator?.elements?.map(
                                            (element) => {
                                                return {
                                                    id: element.id,
                                                    imperialUnits:
                                                        element.imperialUnits,
                                                    metricUnits:
                                                        element.metricUnits,
                                                    optionItemState:
                                                        element.optionItemState,
                                                    text: element.text,
                                                    type: element.type,
                                                    placeholderText: null,
                                                    changeType: null,
                                                    authoredUserId:
                                                        element.authoredUserId,
                                                }
                                            }
                                        ),
                                    }
                                }
                            ) as Partial<TextContentElementInput>[],
                            specifierNotes: [''].concat(
                                template?.contentItem?.comments ?? []
                            ),
                            updateType: 'SpecifierNote',
                        },
                        partType: selectedTopic
                            ? 'CONTENTGROUP'
                            : contentTeamFromStudioPage
                            ? template?.partType?.toLocaleUpperCase()
                            : WorkspaceTabTitles[tab ?? 0],
                        projectId,
                        rootArticleContentItemIds: [template?.articleId ?? ''],
                        productFamilyId: selectedTopic
                            ? selectedTopic?.ppfId
                            : template?.documentTemplateView?.productFamilyId!,
                    },
                }).then(({ data: updatedContentItemElement }) => {
                    //deleting the old dtvcontentitem to handle changes in ids
                    const refetchAPITask = setTimeout(() => {
                        handleRetrieveUpdatedDTV(
                            (updatedContentItemElement?.setProjectContentItemElements as any)
                                ?.partType
                        )
                        retrieveUpdatedElementChanges()
                        clearTimeout(refetchAPITask)
                    }, 0)

                    const dataUpdateTask = setTimeout(() => {
                        contentItemWorker.postMessage({
                            dtv: dtv?.items ?? [],
                            selectedTab: WorkspaceTabTitles[tab ?? 0],
                            workspaceContentItems: contentItemNodes ?? [],
                        })

                        contentItemWorker.onmessage = (event: MessageEvent) => {
                            setWorkspaceContentItems(
                                getUpdatedItemsArray(
                                    workspaceContents()?.contentItemNodes ?? [],
                                    updatedContentItemElement
                                        ?.setProjectContentItemElements
                                        ?.contentItemNodes ?? [],
                                    true,
                                    false
                                ).concat(event.data)
                            )
                        }

                        // reset addNoteSpecifierRequirements and capture 'currentSpecifierNoteId' (to set the focus)
                        setReactiveWorkspaceActions({
                            isAddSpecifierNote: false,
                            addNoteSpecifierRequirements: {
                                currentSpecifierNoteId:
                                    template?.contentItem?.id ?? '',
                            },
                        })

                        clearTimeout(dataUpdateTask)
                    }, 300)
                })
            }

            if (
                contentTeamFromStudioPage &&
                workspaceContents()?.isDraftMode &&
                !isReviewer
            ) {
                const selectedContentItemId = template?.contentItem?.id ?? ''
                if (selectedTemplate?.contentItem?.textType === 'Article') {
                    if (!expandStateList?.includes(selectedContentItemId)) {
                        updateExpandState?.(selectedContentItemId)
                    }
                    setContentItemForNote(selectedContentItemId)
                }

                addSpecifierNoteToProjectContentItemElement()
            }
        },
        [
            expandStateList,
            contentItemForNote,
            setContentItemForNote,
            updateNotesLoading,
        ]
    )

    // side-effect to publish addNoteSpecifier handler
    React.useEffect(() => {
        setReactiveWorkspaceActions({
            addNoteSpecifier,
        })
    }, [addNoteSpecifier])

    return {
        addNoteSpecifier,
        currentSpecifierNoteId,
        optionalHandlers: {
            updateProjectContentItemElement,
            retrieveUpdatedElementChanges,
            handleUpdateTemplate,
            handleRetrieveUpdatedDTV,
            contentItemWorker,
        },
    }
}
